import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getWeekdayLabel, getMonthDayLabel } from "../utils/formatDateLabels";
import { useI18n } from "../i18n/i18n";
import { getLocalizedExerciseName } from "../utils/exerciseName";
import { getAllExercisesCatalog } from "../services/customExerciseService";
import { calculateDayVolume, formatVolumeLoad } from "../utils/volumeLoad";
import { todayYMDLocal } from "../utils/dateUtils";
import DatePromptModal from "./DatePromptModal";

const buildExerciseLookup = () => Object.fromEntries(getAllExercisesCatalog().map((item) => [item.id, item]));

const truncateToTwoWords = (text) => {
    if (typeof text !== "string") {
        return text;
    }

    const words = text.trim().split(/\s+/).filter(Boolean);
    if (words.length <= 2) {
        return text;
    }

    return `${words.slice(0, 2).join(" ")}...`;
};

function collectExercisesForDate({ date, exerciseLog, exerciseMap, t }) {
    return Object.entries(exerciseLog[date] || {}).map(([exerciseId]) => {
        const definition = exerciseMap[exerciseId];
        const fallbackName = t("home.unknownExercise", { id: exerciseId });

        return {
            id: exerciseId,
            date,
            name: definition ? getLocalizedExerciseName(definition, t) : fallbackName,
        };
    });
}

export default function ExerciseProgressTable({
    dates = [],
    exerciseLog = {},
    workoutNotes = {},
    onOpenNoteModal,
}) {
    const { t, locale, localizePath } = useI18n();
    const navigate = useNavigate();
    const exerciseMap = useMemo(buildExerciseLookup, []);
    const todayDate = todayYMDLocal();

    const [datePrompt, setDatePrompt] = useState({
        isOpen: false,
        type: "exercise",
        targetId: "",
        targetName: "",
        targetDate: "",
        selectedChoice: "today",
    });

    const handleCloseDatePrompt = () => {
        setDatePrompt((prev) => ({ ...prev, isOpen: false }));
    };

    const handleConfirmDatePrompt = () => {
        const isTodayChoice = datePrompt.selectedChoice === "today";
        const chosenDate = isTodayChoice ? todayDate : datePrompt.targetDate;
        navigate(localizePath(`/LogExercise/${datePrompt.targetId}/${chosenDate}`));
        setDatePrompt((prev) => ({ ...prev, isOpen: false }));
    };

    const handleExerciseCloneClick = ({ id, name, date }) => {
        if (date === todayDate) {
            navigate(localizePath(`/LogExercise/${id}/${todayDate}`));
        } else {
            setDatePrompt({
                isOpen: true,
                type: "exercise",
                targetId: id,
                targetName: name,
                targetDate: date,
                selectedChoice: "today",
            });
        }
    };

    const rows = useMemo(
        () =>
            dates.map((date) => {
                const note = workoutNotes?.[date];
                const hasNote = typeof note === "string" && note.trim().length > 0;
                return {
                    key: date,
                    weekday: getWeekdayLabel(date, locale),
                    monthDay: getMonthDayLabel(date, locale),
                    dayVolume: calculateDayVolume(exerciseLog?.[date]),
                    exercises: collectExercisesForDate({ date, exerciseLog, exerciseMap, t }),
                    note: hasNote ? note : "",
                    hasNote,
                    isToday: date === todayDate,
                };
            }),
        [dates, locale, exerciseLog, exerciseMap, workoutNotes, t, todayDate]
    );

    return (
        <>
            <table className="compactExerciseTable progressByDateTable" aria-label={t("home.exerciseHistoryTable")}>
                <thead>
                <tr>
                    <th>{t("home.date")}</th>
                    <th>{t("home.exercise")}</th>
                </tr>
                </thead>
                <tbody>
                {rows.map(({ key, weekday, monthDay, dayVolume, exercises, note, hasNote, isToday }) => (
                    <tr key={key}>
                        <td className={`progressDateCell${isToday ? " progressDateCell--today" : ""}`}>
                            {isToday && (
                                <span className="dateHeaderTodayBadge">{t("home.today")}</span>
                            )}
                            <div className="dateHeaderWeekday">{weekday}</div>
                            <div className="dateHeaderDate">{monthDay}</div>
                            {dayVolume > 0 && (
                                <div className="dateVolumeBadge">
                                    {t("home.volumeLoad")}: {formatVolumeLoad(dayVolume, locale)}
                                </div>
                            )}
                            <div className="dateNoteBlock">
                                {hasNote ? (
                                    <button
                                        type="button"
                                        className="noteBadgeButton noteBadgeButton--hasNote"
                                        onClick={() => onOpenNoteModal && onOpenNoteModal({ date: key, note })}
                                        data-date={key}
                                        title={note}
                                        aria-label={t("home.editNoteForDate", { date: key })}
                                    >
                                        <span className="noteBadgeIcon" aria-hidden="true">📝</span>
                                        <span className="noteBadgeText">{truncateToTwoWords(note)}</span>
                                        <span className="noteBadgeActionLabel">{t("home.editNote")}</span>
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        className="noteBadgeButton noteBadgeButton--empty"
                                        onClick={() => onOpenNoteModal && onOpenNoteModal({ date: key, note: "" })}
                                        data-date={key}
                                        aria-label={t("home.addNoteForDate", { date: key })}
                                        title={t("home.addNote")}
                                    >
                                        <span className="noteAddPlus" aria-hidden="true">+</span>
                                        <span className="noteBadgeActionLabel">{t("home.addNote")}</span>
                                    </button>
                                )}
                            </div>
                        </td>
                        <td>
                            <div className="exerciseGroup">
                                {exercises.map(({ id, date, name }) => (
                                    <div key={`${id}-${date}`} className="exerciseRow">
                                        <Link
                                            to={localizePath(`/LogExercise/${id}/${date}`)}
                                            className="exerciseLink"
                                            title={name}
                                        >
                                            <span>{name}</span>
                                        </Link>
                                        <button
                                            type="button"
                                            onClick={() => handleExerciseCloneClick({ id, name, date })}
                                            className="addExerciseButton addExerciseButton--muscleGroup addExerciseButton--inlineExercise"
                                            data-exercise-id={id}
                                            data-date={date}
                                            aria-label={t ? t("home.addExerciseForToday", { name }) : `Add ${name} for today`}
                                            title={t ? t("home.addExerciseForToday", { name }) : `Add ${name} for today`}
                                        >
                                            <span aria-hidden="true">⧉</span>
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </td>
                    </tr>
                ))}
                </tbody>
            </table>
            <DatePromptModal
                isOpen={datePrompt.isOpen}
                type={datePrompt.type}
                targetName={datePrompt.targetName}
                targetDate={datePrompt.targetDate}
                todayDate={todayDate}
                selectedChoice={datePrompt.selectedChoice}
                onChoiceChange={(choice) => setDatePrompt((prev) => ({ ...prev, selectedChoice: choice }))}
                onConfirm={handleConfirmDatePrompt}
                onClose={handleCloseDatePrompt}
            />
        </>
    );
}
